import { useEffect, useState } from "react";
import type RealtimeKitClient from "@cloudflare/realtimekit";
import { ChevronLeft, ChevronRight, Users } from "lucide-react";
import { buildMobileVideoPageView, mobileStreamLimit } from "./mobileVideoPages";

type MobileVideoPageBarProps = {
  meeting: RealtimeKitClient;
  className?: string;
};

type PageSnapshot = {
  people: number;
  currentPage: number;
  pageCount: number;
  maxStreams: number;
};

function readSnapshot(meeting: RealtimeKitClient, viewportWidth: number): PageSnapshot {
  const joined = meeting.participants?.joined?.toArray?.().length ?? 0;
  const selfJoined = meeting.self?.roomJoined ? 1 : 0;
  return {
    people: joined + selfJoined,
    currentPage: meeting.participants?.currentPage ?? 0,
    pageCount: meeting.participants?.pageCount ?? 0,
    maxStreams: mobileStreamLimit(viewportWidth, meeting.self?.config?.maxVideoStreams),
  };
}

const MobileVideoPageBar = ({ meeting, className = "" }: MobileVideoPageBarProps) => {
  const [snapshot, setSnapshot] = useState<PageSnapshot>(() =>
    readSnapshot(meeting, typeof window === "undefined" ? 390 : window.innerWidth),
  );

  useEffect(() => {
    const publish = () => setSnapshot(readSnapshot(meeting, window.innerWidth));
    publish();

    const participants = meeting.participants;
    const joined = participants?.joined;
    joined?.on?.("participantJoined", publish);
    joined?.on?.("participantLeft", publish);
    participants?.on?.("pageChanged", publish);
    participants?.on?.("viewModeChanged", publish);
    meeting.self?.on?.("roomJoined", publish);
    window.addEventListener("resize", publish);

    return () => {
      joined?.removeListener?.("participantJoined", publish);
      joined?.removeListener?.("participantLeft", publish);
      participants?.removeListener?.("pageChanged", publish);
      participants?.removeListener?.("viewModeChanged", publish);
      meeting.self?.removeListener?.("roomJoined", publish);
      window.removeEventListener("resize", publish);
    };
  }, [meeting]);

  const view = buildMobileVideoPageView(snapshot);

  const goPrev = () => {
    if (snapshot.currentPage > 1) {
      void meeting.participants.setPage(snapshot.currentPage - 1);
    }
  };

  const goNext = () => {
    if (snapshot.pageCount > 1 && snapshot.currentPage < snapshot.pageCount) {
      const next = snapshot.currentPage < 1 ? 1 : snapshot.currentPage + 1;
      void meeting.participants.setPage(next);
      return;
    }
    void meeting.participants.setViewMode("PAGINATED");
  };

  return (
    <div
      className={`pointer-events-auto flex max-w-[calc(100%-8.5rem)] items-center gap-1.5 ${className}`}
    >
      <div className="flex items-center gap-1 rounded-full bg-gray-900/90 px-2.5 py-1.5 text-[11px] font-semibold text-white shadow-lg backdrop-blur-sm border border-gray-600/60">
        <Users className="h-3.5 w-3.5 text-blue-300" />
        <span>{view.peopleLabel}</span>
      </div>
      {view.showPager ? (
        <div className="flex items-center gap-1 rounded-full bg-gray-900/90 px-1 py-1 text-[11px] font-semibold text-white shadow-lg backdrop-blur-sm border border-gray-600/60">
          {view.canPrev ? (
            <button
              type="button"
              onClick={goPrev}
              className="flex items-center gap-0.5 rounded-full px-2 py-0.5 hover:bg-gray-700"
              aria-label="Tela anterior"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              Anterior
            </button>
          ) : null}
          {view.pageLabel ? <span className="px-1 tabular-nums">{view.pageLabel}</span> : null}
          {view.canNext ? (
            <button
              type="button"
              onClick={goNext}
              className="flex items-center gap-0.5 rounded-full bg-blue-600 px-2 py-0.5 hover:bg-blue-500"
              aria-label={view.nextLabel}
            >
              {view.nextLabel}
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default MobileVideoPageBar;
